import {Component, inject, computed, WritableSignal, signal, Inject, Signal} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDialogTitle, MatDialogContent } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { UserAvatarComponent } from '../../shared/user-avatar/user-avatar.component';
import { StripeElementsDirective, StripePaymentElementComponent } from 'ngx-stripe';
import { SubscriptionService, SubscriptionPlan } from '../../../services/subscription.service';
import { StripeService } from 'ngx-stripe';
import { AppConstants } from '../../../app-constants';
import {Session} from "../../../services/session.service";
import {ApiCallService} from "../../../services/api-call.service";
import {first} from "rxjs/operators";
import {StripeElementsOptions, StripePaymentElementOptions} from "@stripe/stripe-js";

interface member {
  email: string;
  name: string;
  avatar: string | null;
}

@Component({
  selector: 'app-subscription-add-members',
  standalone: true,
  templateUrl: './subscription-add-members.component.html',
  styleUrls: ['./subscription-add-members.component.scss'],
  imports: [
    MatDialogTitle, MatDialogContent, MatButtonModule, MatIconModule,
    UserAvatarComponent,
    StripeElementsDirective, StripePaymentElementComponent
  ]
})
export class SubscriptionAddMembersComponent {
  private subscriptionService = inject(SubscriptionService);
  private session = inject(Session);
  private readonly api = inject(ApiCallService);
  protected readonly stripe = inject(StripeService);
  dialogRef = inject(MatDialogRef<SubscriptionAddMembersComponent>);
  readonly userEmail = this.session.getUser().userData.email;

  plan: WritableSignal<SubscriptionPlan | null> = signal(null);
  seatCount: WritableSignal<number> = signal(0);
  nextCycleUserAmount: WritableSignal<number> = signal(0);
  nextCycleSubscriptionAmount: WritableSignal<number> = signal(0);
  nextCycleTotalAmount: WritableSignal<number> = signal(0);
  proratedAmount: WritableSignal<number> = signal(0);
  members: WritableSignal<member[]> = signal([]);
  newMembers: WritableSignal<string[]> = signal([]);
  projects: WritableSignal<string[]> = signal([]);
  clientSecret: WritableSignal<string | null> = signal(null);
  customerSessionClientSecret: WritableSignal<string | null> = signal(null);
  errorMessage: WritableSignal<string | null> = signal(null);
  successMessage: WritableSignal<string | null> = signal(null);
  paying: WritableSignal<boolean> = signal(false);
  termsLink = AppConstants.links.terms;
  stripeElementsInstance?: any;

  // Stripe Elements options can be computed here if needed
  stripeElementsOptions: Signal<Partial<StripeElementsOptions>> = computed((): Partial<StripeElementsOptions> => {
    const clientSecret = this.clientSecret();
    const customerSessionClientSecret = this.customerSessionClientSecret();
    return {
      locale: 'en',
      clientSecret: clientSecret ?? undefined,
      customerSessionClientSecret: customerSessionClientSecret ?? undefined,
      appearance: {
        theme: 'stripe',
        labels: 'above',
        variables: {
          colorPrimary: '#292926',
          fontFamily: '"Inter", sans-serif',
          borderRadius: '8px',
        },
      },
    };
  });

  paymentElementOptions = computed<StripePaymentElementOptions> (() => ({
    defaultValues: {
      billingDetails: {
        name: this.session.getUser().userFullName,
        email: this.session.getUser().userData.email,
      },
    },
    layout: {
      type: 'tabs', // 'tabs' or 'accordion' works best for saved methods
      defaultCollapsed: false,
      radios: true, // Use radios for easier selection of saved cards
      spacedAccordionItems: false
    },
    // ADD THIS BLOCK
    displayOrder: ['saved_payment_method', 'card']
  }));

  constructor(@Inject(MAT_DIALOG_DATA) public data: {
    newMembers: string[],
    clientSecret?: string | null,
    customerSessionClientSecret?: string | null,
    proratedAmount?: number,
    nextCycleSubscriptionAmount?: number,
    nextCycleTotalAmount?: number,
    nextCycleUserAmount?: number,
  }) {
    this.fetchPlanAndSeats();
    this.proratedAmount.set(this.data.proratedAmount);
    this.nextCycleUserAmount.set(this.data.nextCycleUserAmount);
    this.nextCycleSubscriptionAmount.set(this.data.nextCycleSubscriptionAmount);
    this.nextCycleTotalAmount.set(this.data.nextCycleTotalAmount);
    this.newMembers.set(data.newMembers);
    this.clientSecret.set(data.clientSecret ?? null);
    this.customerSessionClientSecret.set(data.customerSessionClientSecret ?? null);
  }

  fetchPlanAndSeats() {
    this.subscriptionService.getPlanDetailsAndPricing().subscribe({
      next: ({ plan, seatCount, members, error }) => {
        if (error) {
          this.errorMessage.set(error);
          return;
        }
        this.plan.set(plan);
        this.seatCount.set(seatCount);
        this.members.set(members);
      },
      error: () => {
        this.errorMessage.set('Failed to load plan or seat count.');
      }
    });
  }

  // clientSecret is now provided by the opener dialog data; no remote call here

  onElementsCreated(elements: any) {
    this.stripeElementsInstance = elements;
  }

  pay(): void {
    if (this.paying() || !this.clientSecret() || !this.stripeElementsInstance) {
      this.errorMessage.set('Stripe Elements are not ready.');
      return;
    }
    // ensure prior messages don't overlap
    this.errorMessage.set(null);
    this.successMessage.set(null);
    this.paying.set(true);
    this.stripe.confirmPayment({
      elements: this.stripeElementsInstance,
      // Do not redirect; handle result in-place
      redirect: 'if_required',
    }).subscribe(result => {
      this.paying.set(false);
      if (result.error) {
        this.successMessage.set(null);
        this.errorMessage.set(result.error.message || 'Payment failed. Please try again.');
        return;
      }
      // Consider payment succeeded if a paymentIntent exists and is not in an error state
      const status = (result as any)?.paymentIntent?.status as string | undefined;
      if (!status || status === 'requires_payment_method' || status === 'canceled') {
        this.successMessage.set(null);
        this.errorMessage.set('Payment was not completed. Please try again.');
        return;
      }
      this.errorMessage.set(null);
      this.successMessage.set('Payment successful!');
      // Short delay to let user see the message, then close dialog returning dataset for reload

      this.api.getData('/getHeaderTeam/', null)
        .pipe(first())
        .subscribe((response: any) => {
          if(response && response.success){
            const teamData = response.data[0].dataSet;
            this.dialogRef.close(teamData.teamMembers);
          }
        });
    });
  }

  close(): void {
    this.dialogRef.close();
  }


  // Compute the new monthly amount for the added members
  newMonthlyAmount = computed(() => {
    const plan = this.plan();
    if (!plan) return 0;
    const newMembers = this.newMembers().length;
    return plan.pricePerSeat * newMembers;
  });

  totalDue = computed(() => {
    const plan = this.plan();
    if (!plan) return 0;
    return plan.price + (plan.pricePerSeat * (this.seatCount() + this.newMembers().length));
  });
}
